import type AuthBooking from '@/types/api/types/Auth/Booking'

const TOKYO_TIME_ZONE = 'Asia/Tokyo'

// Front desk messaging opens when the booking becomes active, which the API
// defines as 3 days before the first night (Booking#active?, Booking.active
// scope). Keep this in sync if that window ever changes on the API side.
export const MESSAGING_OPENS_DAYS_BEFORE_CHECKIN = 3

// Today's calendar date in Tokyo, as `YYYY-MM-DD`. `en-CA` is what gives us
// that format, and it sorts lexicographically against the dates the API
// serializes.
export const todayInTokyo = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TOKYO_TIME_ZONE }).format(now)

// `Auth::BookingSerializer` serializes `upcoming_bookings` as
// `guest.bookings.arrival.order(:first_night)`, which has no date filter — a
// stale, never-cancelled arrival booking can sit in there with a check-in date
// in the past. Only a booking that has not started yet counts as upcoming.
export const nextUpcomingBooking = (
  upcomingBookings: AuthBooking[] | undefined | null,
  now: Date = new Date()
): AuthBooking | null => {
  const today = todayInTokyo(now)

  return (
    upcomingBookings?.find(
      booking => booking?.checkinDate && booking.checkinDate >= today
    ) || null
  )
}

// Whether the guest has completed fast check-in for this booking. Completing FCI
// transitions the checkin `started -> pre_checked_in`, which stamps
// `pre_checked_in_at` (`aasm timestamps: true`); the `tagged_bookings` view turns
// that into the `pre_checked_in` tag that `Auth::BookingSerializer` serializes.
//
// Note this is per booking, not per guest: on a group booking the tag flips as
// soon as any one guest has pre-checked in, which is the same semantics as the
// API's own `Booking.pre_checked_in` scope.
export const hasPreCheckedIn = (
  booking: AuthBooking | null | undefined
): boolean => !!booking?.tags?.includes('pre_checked_in')

// The date front desk messaging opens for a given check-in date, as a `Date` at
// midnight in Tokyo so that formatting it in the `Asia/Tokyo` time zone yields
// the intended calendar day everywhere.
export const messagingOpensOn = (
  checkinDate: string | undefined | null
): Date | null => {
  if (!checkinDate) return null

  const openedAt = new Date(`${checkinDate}T00:00:00+09:00`)
  if (Number.isNaN(openedAt.valueOf())) return null

  // UTC arithmetic, not local: `setDate` preserves the local wall clock, so for a
  // viewer in a DST-observing zone a span that crosses a transition lands an hour
  // out and rolls back to the previous JST day. JST has no DST, so subtracting
  // whole UTC days from a Tokyo-midnight instant is exact.
  openedAt.setUTCDate(
    openedAt.getUTCDate() - MESSAGING_OPENS_DAYS_BEFORE_CHECKIN
  )
  return openedAt
}

// Which call to action the messaging screen offers, for a guest who cannot
// message the front desk yet:
//
//   noBooking   - nothing to count down to; invite them to check in if they do
//                 have a booking we don't know about
//   fastCheckin - upcoming stay, check-in still outstanding
//   contact     - upcoming stay, already pre-checked in, so nothing left to do
//                 but reach us another way
export type MessagingCta = 'noBooking' | 'fastCheckin' | 'contact'

export const messagingCta = (
  booking: AuthBooking | null | undefined,
  preCheckedIn: boolean
): MessagingCta => {
  if (!booking) return 'noBooking'

  return preCheckedIn ? 'contact' : 'fastCheckin'
}

// Whether the guest has pre-checked in for their next stay.
//
// Prefers the guest-level `preCheckedInForNextStay`, which the API derives from
// the checkin's *status* scoped to this guest. Falls back to the booking-level
// tag when the field is absent, i.e. an API deployment that predates it — note
// `undefined` falls back while `false` is trusted.
//
// The two disagree in practice: the `tagged_bookings` view computes its
// `pre_checked_in` flag from `checkins.pre_checked_in_at IS NOT NULL`, so a
// checkin whose status was set without the AASM event firing has the status but
// no timestamp, and the tag stays false.
export const preCheckedInForNextStay = (
  guest: { preCheckedInForNextStay?: boolean } | null | undefined,
  booking: AuthBooking | null | undefined
): boolean => guest?.preCheckedInForNextStay ?? hasPreCheckedIn(booking)

// Who the guest should email for help. The property they are about to stay at
// is the more useful contact pre-arrival, but `Property#email` is nullable, and
// older API deployments do not serialize it at all — so fall back to the central
// reservations address, matching the fast check-in flow.
export const contactEmailFor = (
  booking: AuthBooking | null | undefined,
  fallback: string
): string => booking?.property?.email || fallback
